import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import clsx from 'clsx';
import { useState } from 'react';
import { cancelRedeemableStoreCouponHistory } from '../../../apis/redeemableStoreCouponHistories/id/index';
import { RedeemableStoreCouponHistoryTableData } from '../../../types/redeemableStoreCouponHistory/RedeemableStoreCouponHistoryTableData';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import { ActionTableSortParamsType } from '../../../types/table/ActionTableSortParamsType';
import { formatCurrency } from '../../../utils/formatCurrency';
import { TableRoot } from '../../common/Table/TableBase/TableRoot';
import { CancelModal } from '../CancelModal';
import styles from './styles.module.scss';

type Props = {
  rows: RedeemableStoreCouponHistoryTableData[];
  sortParams: ActionTableSortParamsType;
  headerSort: (column: string) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export const RedeemableStoreCouponHistoryTableContent: React.FC<Props> = ({
  rows,
  sortParams,
  headerSort,
  setIsLoading,
}) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [targetIdToCancel, setTargetIdToCancel] = useState<number | null>(null);
  const getNestedValue = (obj: RedeemableStoreCouponHistoryTableData, path: string) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };
  const { showToast } = useToast();

  const onCancel = async () => {
    if (targetIdToCancel === null) return;

    try {
      setIsLoading(true);
      const target = rows.find(row => row.id === targetIdToCancel);
      await cancelRedeemableStoreCouponHistory(target.id, target.three_letter_code);
      showToast({
        message:
          '利用キャンセルが完了しました。※現在の入金金額がキャンセル金額に満たない場合、入金金額がキャンセル金額を上回った時点で自動的に引き落とされます。',
        status: 'success',
      });
    } finally {
      setIsLoading(false);
      setIsCancelModalOpen(false);
    }
  };

  const redeemableStoreCouponHistoryTableColumns: ActionTableColumnsType[] = [
    {
      label: '店舗名',
      key: 'branch',
      sortable: true,
      filterable: true,
    },
    {
      label: '取引金額',
      key: 'amount',
      sortable: true,
      filterable: true,
    },
    {
      label: '発生日時',
      key: 'redeemed_at',
      sortable: true,
      filterable: true,
    },
    {
      label: '',
      key: 'refund',
      sortable: false,
      filterable: false,
    },
  ];

  const tableRows = rows.map((row: RedeemableStoreCouponHistoryTableData, key: number) => (
    <tr key={key} className={clsx(styles.tableRow)} data-testid={`table-row-${key}`}>
      {redeemableStoreCouponHistoryTableColumns.map((column, columnIndex) => (
        <td
          key={column.key}
          className={clsx(styles.tableCell, {
            [styles.clickable]: column.sortable,
            [styles.centerAlign]: column.key === 'refund',
          })}
          onClick={() => {
            if (column.sortable) {
              headerSort(column.key);
            }
          }}
          data-testid={`table-cell-${columnIndex === 0 ? 'no' : column.key}`}
        >
          {column.key === 'refund' ? (
            <button
              type='button'
              className={styles.link}
              onClick={() => {
                setTargetIdToCancel(row.id);
                setIsCancelModalOpen(true);
              }}
              disabled={row.is_cancelled}
            >
              {row.is_cancelled ? 'キャンセル済み' : '利用をキャンセル'}
            </button>
          ) : column.key === 'amount' ? (
            formatCurrency(row.amount)
          ) : (
            (getNestedValue(row, column.key) ?? '-')
          )}
        </td>
      ))}
    </tr>
  ));

  if (rows?.length === 0 || rows === null) {
    return <div className={styles.emptyTable}>データなし</div>;
  }

  return (
    <>
      <TableRoot
        columns={redeemableStoreCouponHistoryTableColumns}
        rows={tableRows}
        sortParams={sortParams}
        onClickHeader={column => headerSort(column)}
      />
      <CancelModal
        isCancelModalOpen={isCancelModalOpen}
        setIsCancelModalOpen={setIsCancelModalOpen}
        onCancel={onCancel}
      />
    </>
  );
};
